/**
 * Notifications settings — per-user in-app mute toggles for the notification bell.
 *
 * Built 2026-08-23 (replaces the Phase-2 placeholder). Scope this pass:
 *   - REAL per-type in-app on/off, backed by notification_preferences +
 *     GET/PATCH /api/notifications/preferences. Toggling a type off stops the
 *     server from writing that notification for this user (gated centrally in
 *     worker/lib/notifications.js createNotification).
 *   - The server owns the controllable-type list; we only supply display copy.
 *   - Storage/deletion warnings are shown as ALWAYS-ON (a data-loss warning must
 *     never be silenceable) — rendered as a locked row, not a real toggle.
 *   - Email delivery is not built yet → shown as a disabled "coming soon" note,
 *     never a fake toggle.
 */

import { Switch } from "@/components/ui/switch";
import { SettingsPageHeader } from "@/pages/settings/settings-placeholder";
import {
  useNotificationPreferences,
  useUpdateNotificationPreference,
  type NotificationPreference,
} from "@/hooks/use-notification-preferences";
import type { NotificationType } from "@/hooks/use-notifications";

/** Display copy per controllable type. Falls back to the raw type if unmapped. */
const TYPE_COPY: Record<string, { label: string; description: string }> = {
  mention: {
    label: "Mentions",
    description: "When someone @mentions you in a comment.",
  },
  comment_reply: {
    label: "Replies",
    description: "When someone replies to a comment you wrote.",
  },
  new_comment: {
    label: "New comments",
    description: "When someone comments on a video you uploaded.",
  },
  video_uploaded: {
    label: "New uploads",
    description: "When a video is uploaded to your workspace.",
  },
  approval_change: {
    label: "Approval changes",
    description: "When the approval status of your video changes.",
  },
};

function copyFor(type: NotificationType): { label: string; description: string } {
  return (
    TYPE_COPY[type] ?? {
      label: type,
      description: "Notifications of this type.",
    }
  );
}

export function NotificationsSettingsPage() {
  return (
    <div>
      <SettingsPageHeader
        title="Notifications"
        subtitle="Choose which activity shows up in your notification bell."
      />

      <SectionLabel title="In-app notifications" />
      <InAppPreferences />

      <div className="mt-10">
        <SectionLabel title="Always on" />
        <LockedRow
          label="Storage & deletion warnings"
          description="Alerts before a video is automatically deleted. Can't be turned off — they protect your footage."
        />
      </div>

      <div className="mt-10">
        <SectionLabel title="Email" />
        <div className="flex items-center justify-between gap-4 rounded-xl border border-dashed border-border bg-secondary/20 px-4 py-3">
          <div className="min-w-0">
            <div className="text-sm font-medium text-foreground">Email delivery</div>
            <div className="text-sm text-muted-foreground">
              Getting these notifications by email is coming soon.
            </div>
          </div>
          <span className="shrink-0 rounded-full border border-muted-foreground/30 bg-muted-foreground/10 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
            Coming soon
          </span>
        </div>
      </div>
    </div>
  );
}

// ── In-app toggle list ───────────────────────────────────────────────────────

function InAppPreferences() {
  const { data: prefs, isLoading, isError } = useNotificationPreferences();
  const update = useUpdateNotificationPreference();

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="h-[62px] animate-pulse rounded-xl border border-border bg-secondary/30"
          />
        ))}
      </div>
    );
  }

  if (isError || !prefs) {
    return (
      <div className="rounded-xl border border-border bg-secondary/30 px-4 py-3 text-sm text-muted-foreground">
        Couldn't load your notification settings. Refresh to try again.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {prefs.map((pref) => (
        <PreferenceRow
          key={pref.type}
          pref={pref}
          pending={update.isPending && update.variables?.type === pref.type}
          onToggle={(inApp) => update.mutate({ type: pref.type, inApp })}
        />
      ))}
    </div>
  );
}

function PreferenceRow({
  pref,
  pending,
  onToggle,
}: {
  pref: NotificationPreference;
  pending: boolean;
  onToggle: (inApp: boolean) => void;
}) {
  const { label, description } = copyFor(pref.type);
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-border bg-secondary/30 px-4 py-3">
      <div className="min-w-0">
        <div className="text-sm font-medium text-foreground">{label}</div>
        <div className="text-sm text-muted-foreground">{description}</div>
      </div>
      <Switch
        checked={pref.inApp}
        disabled={pending}
        onCheckedChange={onToggle}
        aria-label={`${label} in-app notifications`}
      />
    </div>
  );
}

// ── Locked (always-on) row ───────────────────────────────────────────────────

function LockedRow({ label, description }: { label: string; description: string }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-border bg-secondary/30 px-4 py-3">
      <div className="min-w-0">
        <div className="text-sm font-medium text-foreground">{label}</div>
        <div className="text-sm text-muted-foreground">{description}</div>
      </div>
      <Switch checked disabled aria-label={`${label} (always on)`} />
    </div>
  );
}

// ── Section label (matches profile.tsx) ──────────────────────────────────────

function SectionLabel({ title }: { title: string }) {
  return (
    <div className="mb-3">
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
    </div>
  );
}
